import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { 
  DollarSign, 
  CreditCard, 
  CheckCircle2, 
  AlertTriangle, 
  Clock, 
  TrendingUp, 
  TrendingDown,
  RefreshCw,
  Bell,
  Link,
  Package,
  ArrowUpRight,
  ArrowDownRight,
  Loader2
} from 'lucide-react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar
} from 'recharts';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminPaymentMetrics() {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState(null);
  const [hourlyData, setHourlyData] = useState([]);
  const [failedPayments, setFailedPayments] = useState({ failed_payment_cases: [], suspicious_transactions: [], total_needing_attention: 0 });
  const [linkStats, setLinkStats] = useState(null);
  const [productRevenue, setProductRevenue] = useState([]);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const headers = { 'Authorization': `Bearer ${token}` };
      
      const [overviewRes, hourlyRes, failedRes, linksRes, productsRes] = await Promise.all([
        fetch(`${API_URL}/api/payment-metrics/overview`, { headers }),
        fetch(`${API_URL}/api/payment-metrics/hourly?hours=24`, { headers }),
        fetch(`${API_URL}/api/payment-metrics/failed-payments`, { headers }),
        fetch(`${API_URL}/api/payment-metrics/payment-links-stats`, { headers }),
        fetch(`${API_URL}/api/payment-metrics/products-revenue?days=30`, { headers })
      ]);

      if (overviewRes.ok) setOverview(await overviewRes.json());
      if (hourlyRes.ok) {
        const data = await hourlyRes.json();
        setHourlyData(data.data || []);
      }
      if (failedRes.ok) setFailedPayments(await failedRes.json());
      if (linksRes.ok) setLinkStats(await linksRes.json());
      if (productsRes.ok) {
        const data = await productsRes.json();
        setProductRevenue(data.products || []);
      }

      setLastUpdated(new Date());
    } catch (error) {
      console.error('Error fetching metrics:', error);
      toast.error('Error al cargar métricas');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchData();
    
    // Auto-refresh every 30 seconds
    let interval;
    if (autoRefresh) {
      interval = setInterval(fetchData, 30000);
    }
    
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [fetchData, autoRefresh]);

  const handleResolve = async (caseId) => {
    try {
      const res = await fetch(`${API_URL}/api/payment-metrics/resolve/${caseId}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        toast.success('Caso marcado como resuelto');
        fetchData();
      } else {
        toast.error('Error al resolver caso');
      }
    } catch (error) {
      toast.error('Error al resolver caso');
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'USD' }).format(amount);
  };

  const getChangeIcon = (change) => {
    if (change > 0) return <ArrowUpRight className="w-4 h-4 text-green-500" />;
    if (change < 0) return <ArrowDownRight className="w-4 h-4 text-red-500" />;
    return null;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  const alertCount = failedPayments.total_needing_attention;

  return (
    <div className="p-6 space-y-6 bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Métricas de Pagos</h1>
          <p className="text-sm text-gray-500">
            Monitoreo en tiempo real del flujo de pagos
            {lastUpdated && ` • Actualizado: ${lastUpdated.toLocaleTimeString()}`}
          </p>
        </div>
        <div className="flex items-center gap-3">
          {alertCount > 0 && (
            <Badge variant="destructive" className="animate-pulse">
              <Bell className="w-3 h-3 mr-1" />
              {alertCount} alertas
            </Badge>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={() => setAutoRefresh(!autoRefresh)}
            className={autoRefresh ? 'bg-green-50 border-green-200' : ''}
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${autoRefresh ? 'animate-spin' : ''}`} />
            {autoRefresh ? 'Auto' : 'Manual'}
          </Button>
          <Button variant="outline" size="sm" onClick={fetchData}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Actualizar
          </Button>
        </div>
      </div>

      {/* KPI Cards */}
      {overview && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          {/* Revenue Today */}
          <Card className="bg-gradient-to-br from-green-500 to-green-600 text-white">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium opacity-90">Ingresos Hoy</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{formatCurrency(overview.today.revenue)}</div>
              <div className="flex items-center text-xs mt-1 opacity-80">
                {getChangeIcon(overview.comparisons.revenue_change)}
                <span className="ml-1">
                  {overview.comparisons.revenue_change >= 0 ? '+' : ''}{formatCurrency(overview.comparisons.revenue_change)} vs ayer
                </span>
              </div>
            </CardContent>
          </Card>

          {/* Payments Completed */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-gray-500 flex items-center">
                <CreditCard className="w-4 h-4 mr-2" />
                Pagos Completados
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{overview.today.payments_completed}</div>
              <div className="flex items-center text-xs text-gray-500 mt-1">
                {getChangeIcon(overview.comparisons.payments_change)}
                <span className="ml-1">
                  {overview.comparisons.payments_change >= 0 ? '+' : ''}{overview.comparisons.payments_change} vs ayer
                </span>
              </div>
            </CardContent>
          </Card>

          {/* Cases Created */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-gray-500 flex items-center">
                <CheckCircle2 className="w-4 h-4 mr-2 text-green-500" />
                Casos Creados
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{overview.today.cases_created}</div>
              <div className="text-xs text-gray-500 mt-1">Hoy</div>
            </CardContent>
          </Card>

          {/* Success Rate */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-gray-500 flex items-center">
                <TrendingUp className="w-4 h-4 mr-2 text-blue-500" />
                Tasa de Éxito
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{overview.today.success_rate}%</div>
              <div className="text-xs text-gray-500 mt-1">Pago → Caso</div>
            </CardContent>
          </Card>

          {/* Alerts */}
          <Card className={alertCount > 0 ? 'border-red-200 bg-red-50' : ''}>
            <CardHeader className="pb-2">
              <CardTitle className={`text-sm font-medium flex items-center ${alertCount > 0 ? 'text-red-600' : 'text-gray-500'}`}>
                <AlertTriangle className={`w-4 h-4 mr-2 ${alertCount > 0 ? 'text-red-500' : ''}`} />
                Requieren Atención
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className={`text-2xl font-bold ${alertCount > 0 ? 'text-red-600' : ''}`}>
                {alertCount}
              </div>
              <div className="text-xs text-gray-500 mt-1">
                {overview.alerts.pending_webhooks} pendientes
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Hourly Chart */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Pagos por Hora (24h)</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={hourlyData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="hour" fontSize={12} />
                  <YAxis fontSize={12} />
                  <Tooltip 
                    contentStyle={{ backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px' }}
                    formatter={(value, name) => [value, name === 'completed' ? 'Completados' : 'Iniciados']}
                  />
                  <Line type="monotone" dataKey="initiated" stroke="#94a3b8" strokeWidth={2} dot={false} name="Iniciados" />
                  <Line type="monotone" dataKey="completed" stroke="#22c55e" strokeWidth={2} dot={false} name="Completados" />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Product Revenue */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center">
              <Package className="w-5 h-5 mr-2" />
              Ingresos por Producto (30 días)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={productRevenue} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis type="number" fontSize={12} tickFormatter={(v) => `$${v}`} />
                  <YAxis type="category" dataKey="product_name" fontSize={11} width={100} />
                  <Tooltip 
                    contentStyle={{ backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px' }}
                    formatter={(value) => [formatCurrency(value), 'Ingresos']}
                  />
                  <Bar dataKey="revenue" fill="#3b82f6" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Payment Links Stats */}
      {linkStats && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center">
              <Link className="w-5 h-5 mr-2" />
              Estadísticas de Payment Links
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
              <div className="text-center p-3 bg-gray-50 rounded-lg">
                <div className="text-2xl font-bold">{linkStats.total_links}</div>
                <div className="text-xs text-gray-500">Total Links</div>
              </div>
              <div className="text-center p-3 bg-blue-50 rounded-lg">
                <div className="text-2xl font-bold text-blue-600">{linkStats.links_this_week}</div>
                <div className="text-xs text-gray-500">Esta Semana</div>
              </div>
              <div className="text-center p-3 bg-green-50 rounded-lg">
                <div className="text-2xl font-bold text-green-600">{linkStats.paid_links}</div>
                <div className="text-xs text-gray-500">Pagados</div>
              </div>
              <div className="text-center p-3 bg-yellow-50 rounded-lg">
                <div className="text-2xl font-bold text-yellow-600">{linkStats.pending_links}</div>
                <div className="text-xs text-gray-500">Pendientes</div>
              </div>
              <div className="text-center p-3 bg-red-50 rounded-lg">
                <div className="text-2xl font-bold text-red-600">{linkStats.expired_links}</div>
                <div className="text-xs text-gray-500">Expirados</div>
              </div>
              <div className="text-center p-3 bg-purple-50 rounded-lg">
                <div className="text-2xl font-bold text-purple-600">{linkStats.conversion_rate}%</div>
                <div className="text-xs text-gray-500">Conversión</div>
              </div>
              <div className="text-center p-3 bg-gradient-to-br from-green-100 to-green-50 rounded-lg">
                <div className="text-2xl font-bold text-green-700">{formatCurrency(linkStats.total_revenue)}</div>
                <div className="text-xs text-gray-500">Ingresos</div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Failed Payments / Alerts */}
      {alertCount > 0 && (
        <Card className="border-red-200">
          <CardHeader className="bg-red-50 rounded-t-lg">
            <CardTitle className="text-lg text-red-700 flex items-center">
              <AlertTriangle className="w-5 h-5 mr-2" />
              Pagos que Requieren Atención ({alertCount})
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="divide-y">
              {/* Failed Payment Cases */}
              {failedPayments.failed_payment_cases.map((item, idx) => (
                <div key={`failed-${idx}`} className="p-4 hover:bg-gray-50 flex items-center justify-between">
                  <div>
                    <div className="flex items-center gap-2">
                      <Badge variant="destructive">Error Crítico</Badge>
                      <span className="font-medium">{item.metadata?.client_email || 'Email desconocido'}</span>
                    </div>
                    <p className="text-sm text-gray-500 mt-1">
                      {item.metadata?.product_name || 'Producto desconocido'} • {item.error}
                    </p>
                    <p className="text-xs text-gray-400">{item.created_at}</p>
                  </div>
                  <div className="flex gap-2">
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={() => window.open('/admin/casos-avanzado', '_blank')}
                    >
                      Crear Caso
                    </Button>
                    <Button 
                      size="sm" 
                      variant="ghost"
                      onClick={() => handleResolve(item.id)}
                    >
                      Resolver
                    </Button>
                  </div>
                </div>
              ))}
              
              {/* Suspicious Transactions */}
              {failedPayments.suspicious_transactions.map((item, idx) => (
                <div key={`suspicious-${idx}`} className="p-4 hover:bg-gray-50 flex items-center justify-between">
                  <div>
                    <div className="flex items-center gap-2">
                      <Badge variant="secondary" className="bg-yellow-100 text-yellow-700">Sospechoso</Badge>
                      <span className="font-medium">{item.client_email}</span>
                    </div>
                    <p className="text-sm text-gray-500 mt-1">
                      {item.product_name} • {formatCurrency(item.amount)} • {item.reason}
                    </p>
                    <p className="text-xs text-gray-400">{item.paid_at}</p>
                  </div>
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={() => window.open('/admin/casos-avanzado', '_blank')}
                  >
                    Revisar
                  </Button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* No Alerts */}
      {alertCount === 0 && (
        <Card className="border-green-200 bg-green-50">
          <CardContent className="py-8 text-center">
            <CheckCircle2 className="w-12 h-12 text-green-500 mx-auto mb-3" />
            <h3 className="text-lg font-semibold text-green-700">Todo en Orden</h3>
            <p className="text-green-600">No hay pagos que requieran atención manual</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
